// ---------------------------------------------------------------------------
// Prezio Onboard Widget — LanguageMenu
// ---------------------------------------------------------------------------
// Header language switcher: English plus the languages the backend lists for
// the session's country (`country_languages`). Shown once a country is chosen.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSessionActions, useSessionState } from '@/hooks/useSession';
import { t, toWidgetLocale } from '@/i18n';
import { DEFAULT_LOCALE } from '@/utils/constants';
import { Globe } from '@/utils/stepIcons';
import type { Locale } from '@/types/config';
import styles from './LanguageMenu.module.css';

/**
 * A language's name in that language, capitalised ("Français"). Falls back to
 * the upper-cased code when the runtime lacks `Intl.DisplayNames` (older WebViews).
 */
function languageName(code: Locale): string {
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code) ?? code;
    return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
  } catch {
    return code.toUpperCase();
  }
}

export function LanguageMenu() {
  const { session, isSubmitting, locale } = useSessionState();
  const { changeLocale } = useSessionActions();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const countryLanguages = session?.country_languages;
  const locales = useMemo(() => {
    const country = (countryLanguages ?? [])
      .map(toWidgetLocale)
      .filter((code): code is Locale => code !== null);
    return [...new Set<Locale>([DEFAULT_LOCALE, ...country])];
  }, [countryLanguages]);

  useEffect(() => {
    if (!open) return;
    // composedPath() sees through a shadow root, where `target` is the host.
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !event.composedPath().includes(rootRef.current)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const step = session?.current_step;
  const hasCountry = Boolean(session?.collected_data['country_code']);
  // Hidden when it would only offer the current language.
  const hasChoice = locales.some((code) => code !== locale);
  if (!step || step === 'country' || !hasCountry || !hasChoice) return null;

  const handleSelect = (code: Locale) => {
    setOpen(false);
    if (code !== locale) void changeLocale(code);
  };

  return (
    <div ref={rootRef} className={styles['root']}>
      <button
        type="button"
        className={styles['trigger']}
        onClick={() => setOpen((value) => !value)}
        disabled={isSubmitting}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('common.changeLanguage')}
      >
        <Globe />
        <span className={styles['code']}>{locale.toUpperCase()}</span>
      </button>

      {open && (
        <ul role="menu" className={styles['menu']}>
          {locales.map((code) => (
            <li key={code} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={code === locale}
                lang={code}
                className={styles['item']}
                onClick={() => handleSelect(code)}
              >
                <span>{languageName(code)}</span>
                {code === locale && <CheckIcon />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M4 10.5L8 14.5L16 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
